import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, GraduationCap, Save, Send, Info, Plus, Trash2, ArrowUp, ArrowDown, Layers, CheckCircle2, Film } from "lucide-react";
import { toast } from "sonner";
import {
  COURSE_CATEGORIES,
  NIVELES_CURSO,
  IDIOMAS_CURSO,
  TIPOS_LECCION,
  MKT_GOLD,
  MKT_TINT,
} from "../../lib/marketplace";
import { apiGetCursos, apiCrearCurso, apiEditarCurso } from "../../lib/marketplaceApi";
import { humanizeAxiosError } from "../../lib/api";
import { Field, TextInput, TextArea, SelectInput, ImageUpload, FileUpload, Stepper, COURSE_NOTICE } from "./mktui";
import ComisionResumen from "./ComisionResumen";

const PASOS = ["Información general", "Contenido del curso", "Precio y publicación", "Revisión final"];
const uid = (p) => `${p}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
const catLabel = (id) => COURSE_CATEGORIES.find((c) => c.id === id)?.label || id;
const tipoLabel = (id) => TIPOS_LECCION.find((t) => t.id === id)?.label || id;

export default function CrearCurso() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("id");
  const [existente, setExistente] = useState(null);

  const [paso, setPaso] = useState(0);
  const [exito, setExito] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({
    titulo: "", subtitulo: "", descCorta: "", descCompleta: "", categoria: "", nivel: "", idioma: "Español",
    cover: "", videoPresentacion: "", etiquetas: "",
    modulos: [{ id: uid("m"), titulo: "Módulo 1", lecciones: [] }],
    gratuito: false, precio: "", precioPromo: "", accesoVida: true, certificado: false,
    autoria: false, derechos: false, terminos: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const videoRef = useRef(null);

  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const items = await apiGetCursos();
        const c = items.find((x) => x.id === editId);
        if (c) {
          setExistente(c);
          setForm({
            titulo: c.titulo || "", subtitulo: c.subtitulo || "", descCorta: c.descCorta || "", descCompleta: c.descCompleta || "",
            categoria: c.categoria || "", nivel: c.nivel || "", idioma: c.idioma || "Español",
            cover: c.cover || "", videoPresentacion: c.videoPresentacion || "", etiquetas: c.etiquetas || "",
            modulos: (c.modulos && c.modulos.length ? c.modulos : [{ id: uid("m"), titulo: "Módulo 1", lecciones: [] }]),
            gratuito: !!c.gratuito, precio: c.precio ?? "", precioPromo: c.precioPromo ?? "", accesoVida: c.accesoVida !== false,
            certificado: !!c.certificado, autoria: !!c.autoria, derechos: !!c.derechos, terminos: !!c.terminos,
          });
        }
      } catch (e) { /* noop */ }
    })();
  }, [editId]);

  /* ---- módulos / lecciones ---- */
  const addModulo = () => set("modulos", [...form.modulos, { id: uid("m"), titulo: `Módulo ${form.modulos.length + 1}`, lecciones: [] }]);
  const rmModulo = (mid) => set("modulos", form.modulos.filter((m) => m.id !== mid));
  const setModulo = (mid, k, v) => set("modulos", form.modulos.map((m) => (m.id === mid ? { ...m, [k]: v } : m)));
  const moveModulo = (i, dir) => {
    const arr = [...form.modulos];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set("modulos", arr);
  };
  const addLeccion = (mid) =>
    set("modulos", form.modulos.map((m) => (m.id === mid ? { ...m, lecciones: [...m.lecciones, { id: uid("l"), tipo: "video", titulo: "", descripcion: "", duracion: "", recurso: "" }] } : m)));
  const setLeccion = (mid, lid, k, v) =>
    set("modulos", form.modulos.map((m) => (m.id === mid ? { ...m, lecciones: m.lecciones.map((l) => (l.id === lid ? { ...l, [k]: v } : l)) } : m)));
  const rmLeccion = (mid, lid) =>
    set("modulos", form.modulos.map((m) => (m.id === mid ? { ...m, lecciones: m.lecciones.filter((l) => l.id !== lid) } : m)));
  const moveLeccion = (mid, i, dir) =>
    set("modulos", form.modulos.map((m) => {
      if (m.id !== mid) return m;
      const arr = [...m.lecciones];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return m;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...m, lecciones: arr };
    }));

  const totalLecciones = form.modulos.reduce((n, m) => n + m.lecciones.length, 0);

  const validarPaso = () => {
    if (paso === 0) {
      if (!form.titulo.trim()) return "Ingresa el título del curso.";
      if (!form.categoria) return "Selecciona una categoría.";
      if (!form.nivel) return "Selecciona el nivel.";
    }
    return null;
  };
  const siguiente = () => {
    const err = validarPaso();
    if (err) return toast.error(err);
    setPaso((p) => Math.min(p + 1, PASOS.length - 1));
  };

  const guardar = async (estado) => {
    if (!form.titulo.trim()) return toast.error("Ingresa el título del curso.");
    if (estado === "en_revision") {
      if (!form.categoria || !form.nivel) return toast.error("Completa la información general.");
      if (!form.gratuito && !form.precio) return toast.error("Ingresa el precio o marca el curso como gratuito.");
      if (!form.autoria || !form.derechos || !form.terminos) return toast.error("Debes confirmar autoría, derechos y aceptar los términos.");
    }
    const payload = {
      ...form,
      precio: form.gratuito || form.precio === "" ? 0 : Number(form.precio),
      precioPromo: form.precioPromo === "" || form.precioPromo == null ? null : Number(form.precioPromo),
    };
    setGuardando(true);
    try {
      if (existente) await apiEditarCurso(existente.id, payload, estado === "en_revision" ? "en_revision" : undefined);
      else await apiCrearCurso(payload, estado);
      if (estado === "borrador") {
        toast.success("Curso guardado como borrador.");
        navigate("/marketplace/cursos");
      } else {
        setExito(true);
      }
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setGuardando(false);
    }
  };

  const readVideo = (e) => {
    const f = e.target.files?.[0];
    if (f) set("videoPresentacion", f.name);
  };

  if (exito) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-200 shadow-soft p-8 text-center" data-testid="curso-exito">
          <span className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: "#FEF3C7", color: "#B45309" }}>
            <CheckCircle2 size={34} />
          </span>
          <h1 className="mt-5 text-2xl font-extrabold text-slate-900">¡Curso enviado a revisión!</h1>
          <p className="mt-2 text-sm text-slate-500">Revisaremos tu curso antes de publicarlo. Podrás ver su estado y los comentarios en <b>Mis Cursos</b>.</p>
          <button onClick={() => navigate("/marketplace/cursos")} data-testid="curso-exito-btn" className="mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
            Ir a Mis Cursos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="crear-curso-page">
        <button onClick={() => navigate("/marketplace/cursos")} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="cc-volver-top">
          <ChevronLeft size={16} /> Volver a Mis Cursos
        </button>

        <div className="mt-5 flex items-center gap-4">
          <span className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm" style={{ backgroundColor: MKT_TINT, color: MKT_GOLD }}>
            <GraduationCap size={26} />
          </span>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: MKT_GOLD }}>ConstruLink Marketplace</p>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">{existente ? "Editar curso" : "Crear curso independiente"}</h1>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-xl p-3">
          <Info size={15} className="mt-0.5 flex-shrink-0" style={{ color: MKT_GOLD }} />
          {COURSE_NOTICE}
        </div>

        <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-soft p-5">
          <Stepper pasos={PASOS} actual={paso} />
        </div>

        <div className="mt-5 bg-white rounded-2xl border border-slate-200 shadow-soft p-6 space-y-5">
          {/* PASO 1 */}
          {paso === 0 && (
            <div className="space-y-5" data-testid="cc-paso-general">
              <Field label="Título del curso" required testid="cc-titulo-field">
                <TextInput value={form.titulo} onChange={(v) => set("titulo", v)} placeholder="Ej. Metrados y presupuestos desde cero" testid="cc-titulo" />
              </Field>
              <Field label="Subtítulo" testid="cc-subtitulo-field">
                <TextInput value={form.subtitulo} onChange={(v) => set("subtitulo", v)} placeholder="Una frase que resuma tu curso" testid="cc-subtitulo" />
              </Field>
              <Field label="Descripción corta" testid="cc-desccorta-field">
                <TextInput value={form.descCorta} onChange={(v) => set("descCorta", v)} placeholder="Resumen breve" testid="cc-desccorta" />
              </Field>
              <Field label="Descripción completa" testid="cc-desccompleta-field">
                <TextArea value={form.descCompleta} onChange={(v) => set("descCompleta", v)} rows={4} placeholder="Explica qué aprenderán los estudiantes…" testid="cc-desccompleta" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Field label="Categoría" required testid="cc-categoria-field">
                  <SelectInput value={form.categoria} onChange={(v) => set("categoria", v)} options={COURSE_CATEGORIES.map((c) => ({ value: c.id, label: c.label }))} placeholder="Categoría" testid="cc-categoria" />
                </Field>
                <Field label="Nivel" required testid="cc-nivel-field">
                  <SelectInput value={form.nivel} onChange={(v) => set("nivel", v)} options={NIVELES_CURSO} placeholder="Nivel" testid="cc-nivel" />
                </Field>
                <Field label="Idioma" testid="cc-idioma-field">
                  <SelectInput value={form.idioma} onChange={(v) => set("idioma", v)} options={IDIOMAS_CURSO} placeholder="Idioma" testid="cc-idioma" />
                </Field>
              </div>
              <Field label="Imagen de portada" testid="cc-cover-field">
                <ImageUpload value={form.cover} onChange={(v) => set("cover", v)} scope="course_cover" aspect="aspect-[16/9]" label="Subir portada" testid="cc-cover" />
              </Field>
              <Field label="Video de presentación" hint="Opcional · la subida real se habilitará más adelante" testid="cc-video-field">
                <button type="button" onClick={() => videoRef.current?.click()} className="w-full rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-sm font-semibold text-slate-500 hover:border-amber-400 hover:text-amber-600 flex items-center justify-center gap-2" data-testid="cc-video-btn">
                  <Film size={16} /> {form.videoPresentacion || "Seleccionar video de presentación"}
                </button>
                <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={readVideo} />
              </Field>
              <Field label="Etiquetas" hint="Separadas por comas" testid="cc-etiquetas-field">
                <TextInput value={form.etiquetas} onChange={(v) => set("etiquetas", v)} placeholder="metrados, presupuesto, s10" testid="cc-etiquetas" />
              </Field>
            </div>
          )}

          {/* PASO 2 */}
          {paso === 1 && (
            <div className="space-y-4" data-testid="cc-paso-contenido">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-slate-700 inline-flex items-center gap-2"><Layers size={16} style={{ color: MKT_GOLD }} /> Estructura del curso</p>
                <button onClick={addModulo} data-testid="cc-add-modulo" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: MKT_GOLD }}>
                  <Plus size={15} /> Agregar módulo
                </button>
              </div>

              {form.modulos.map((m, mi) => (
                <div key={m.id} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid={`cc-modulo-${mi}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold px-2 py-1 rounded-md" style={{ backgroundColor: MKT_TINT, color: "#92400E" }}>M{mi + 1}</span>
                    <input value={m.titulo} onChange={(e) => setModulo(m.id, "titulo", e.target.value)} placeholder="Título del módulo" className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-amber-500" data-testid={`cc-modulo-titulo-${mi}`} />
                    <button onClick={() => moveModulo(mi, -1)} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100" title="Subir"><ArrowUp size={15} /></button>
                    <button onClick={() => moveModulo(mi, 1)} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100" title="Bajar"><ArrowDown size={15} /></button>
                    <button onClick={() => rmModulo(m.id)} className="p-2 rounded-lg text-red-500 hover:bg-red-50" title="Eliminar módulo" data-testid={`cc-rm-modulo-${mi}`}><Trash2 size={15} /></button>
                  </div>

                  <div className="mt-3 space-y-3">
                    {m.lecciones.map((l, li) => (
                      <div key={l.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`cc-leccion-${mi}-${li}`}>
                        <div className="flex items-center gap-2">
                          <select value={l.tipo} onChange={(e) => setLeccion(m.id, l.id, "tipo", e.target.value)} className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold outline-none focus:border-amber-500" data-testid={`cc-leccion-tipo-${mi}-${li}`}>
                            {TIPOS_LECCION.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                          </select>
                          <input value={l.titulo} onChange={(e) => setLeccion(m.id, l.id, "titulo", e.target.value)} placeholder="Título de la lección" className="flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-amber-500" data-testid={`cc-leccion-titulo-${mi}-${li}`} />
                          <input value={l.duracion} onChange={(e) => setLeccion(m.id, l.id, "duracion", e.target.value)} placeholder="10 min" className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-amber-500" data-testid={`cc-leccion-duracion-${mi}-${li}`} />
                          <button onClick={() => moveLeccion(m.id, li, -1)} className="p-1.5 rounded text-slate-400 hover:bg-slate-100"><ArrowUp size={13} /></button>
                          <button onClick={() => moveLeccion(m.id, li, 1)} className="p-1.5 rounded text-slate-400 hover:bg-slate-100"><ArrowDown size={13} /></button>
                          <button onClick={() => rmLeccion(m.id, l.id)} className="p-1.5 rounded text-red-500 hover:bg-red-50"><Trash2 size={13} /></button>
                        </div>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input value={l.descripcion} onChange={(e) => setLeccion(m.id, l.id, "descripcion", e.target.value)} placeholder="Descripción (opcional)" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-amber-500" />
                          {l.tipo === "video" ? (
                            <input
                              value={l.recurso || ""}
                              onChange={(e) => { setLeccion(m.id, l.id, "recurso", e.target.value); setLeccion(m.id, l.id, "recursoNombre", e.target.value ? "Video (enlace externo)" : ""); }}
                              placeholder="URL del video (YouTube, Vimeo, enlace embebido)"
                              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-amber-500"
                              data-testid={`cc-leccion-video-url-${mi}-${li}`}
                            />
                          ) : (
                            <FileUpload
                              value={l.recurso}
                              nombre={l.recursoNombre}
                              onChange={(url, nombre) => { setLeccion(m.id, l.id, "recurso", url); setLeccion(m.id, l.id, "recursoNombre", nombre); }}
                              scope="course_doc"
                              accept=".pdf,.docx,.xlsx,.pptx,.zip,.rar,.dwg,.rvt,.rfa,.ifc,.dyn,.pbix"
                              label="Documento descargable (máx. 20 MB)"
                              testid={`cc-leccion-material-${mi}-${li}`}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addLeccion(m.id)} data-testid={`cc-add-leccion-${mi}`} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-slate-300 text-sm font-semibold text-slate-500 hover:border-amber-400 hover:text-amber-600">
                      <Plus size={14} /> Agregar lección
                    </button>
                  </div>
                </div>
              ))}

              <button onClick={() => guardar("borrador")} className="text-sm font-semibold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1.5" data-testid="cc-contenido-borrador">
                <Save size={14} /> Guardar como borrador
              </button>
            </div>
          )}

          {/* PASO 3 */}
          {paso === 2 && (
            <div className="space-y-5" data-testid="cc-paso-precio">
              <div className="flex gap-3">
                <button onClick={() => set("gratuito", true)} data-testid="cc-gratuito" className="flex-1 rounded-xl border p-4 text-left transition-all" style={form.gratuito ? { borderColor: MKT_GOLD, boxShadow: `0 0 0 2px ${MKT_GOLD}22` } : { borderColor: "#E2E8F0" }}>
                  <p className="font-bold text-slate-900">Curso gratuito</p>
                  <p className="text-xs text-slate-500 mt-0.5">Acceso libre para todos</p>
                </button>
                <button onClick={() => set("gratuito", false)} data-testid="cc-pago" className="flex-1 rounded-xl border p-4 text-left transition-all" style={!form.gratuito ? { borderColor: MKT_GOLD, boxShadow: `0 0 0 2px ${MKT_GOLD}22` } : { borderColor: "#E2E8F0" }}>
                  <p className="font-bold text-slate-900">Curso de pago</p>
                  <p className="text-xs text-slate-500 mt-0.5">Define un precio en soles</p>
                </button>
              </div>

              {!form.gratuito && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Precio en soles (S/)" required testid="cc-precio-field">
                    <TextInput value={form.precio} onChange={(v) => set("precio", v)} type="number" placeholder="0.00" testid="cc-precio" />
                  </Field>
                  <Field label="Precio promocional (S/)" hint="Opcional" testid="cc-preciopromo-field">
                    <TextInput value={form.precioPromo} onChange={(v) => set("precioPromo", v)} type="number" placeholder="0.00" testid="cc-preciopromo" />
                  </Field>
                </div>
              )}
              {!form.gratuito && <ComisionResumen precio={form.precio} gratuito={form.gratuito} />}

              <div className="space-y-2">
                <label className="flex items-center gap-3 text-sm text-slate-700"><input type="checkbox" checked={form.accesoVida} onChange={(e) => set("accesoVida", e.target.checked)} className="w-5 h-5 accent-amber-600" data-testid="cc-accesovida" /> Acceso de por vida</label>
                <label className="flex items-center gap-3 text-sm text-slate-700"><input type="checkbox" checked={form.certificado} onChange={(e) => set("certificado", e.target.checked)} className="w-5 h-5 accent-amber-600" data-testid="cc-certificado" /> Ofrecer certificado del instructor al finalizar</label>
              </div>

              <div className="space-y-2 pt-2 border-t border-slate-100">
                <label className="flex items-start gap-3 text-sm text-slate-600"><input type="checkbox" checked={form.autoria} onChange={(e) => set("autoria", e.target.checked)} className="mt-0.5 w-5 h-5 accent-amber-600" data-testid="cc-autoria" /> Confirmo la <b className="text-slate-800">autoría</b> del contenido del curso.</label>
                <label className="flex items-start gap-3 text-sm text-slate-600"><input type="checkbox" checked={form.derechos} onChange={(e) => set("derechos", e.target.checked)} className="mt-0.5 w-5 h-5 accent-amber-600" data-testid="cc-derechos" /> Confirmo que tengo los <b className="text-slate-800">derechos de uso</b> de todo el material.</label>
                <label className="flex items-start gap-3 text-sm text-slate-600"><input type="checkbox" checked={form.terminos} onChange={(e) => set("terminos", e.target.checked)} className="mt-0.5 w-5 h-5 accent-amber-600" data-testid="cc-terminos" /> Acepto los <b className="text-slate-800">términos para instructores independientes</b>.</label>
              </div>
            </div>
          )}

          {/* PASO 4 */}
          {paso === 3 && (
            <div className="space-y-4" data-testid="cc-paso-revision">
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="h-32 flex items-center justify-center" style={{ background: form.cover ? undefined : `linear-gradient(135deg, #FFFFFF, ${MKT_TINT})` }}>
                  {form.cover ? <img src={form.cover} alt="portada" className="w-full h-full object-cover" /> : <GraduationCap size={34} style={{ color: MKT_GOLD }} />}
                </div>
                <div className="p-4">
                  <p className="text-[10px] font-bold uppercase" style={{ color: MKT_GOLD }}>{form.categoria ? catLabel(form.categoria) : "Sin categoría"} · {form.nivel || "—"}</p>
                  <h3 className="mt-1 text-lg font-extrabold text-slate-900">{form.titulo || "Sin título"}</h3>
                  {form.subtitulo && <p className="text-sm text-slate-500">{form.subtitulo}</p>}
                  <p className="mt-2 font-extrabold text-slate-900">{form.gratuito ? "Gratis" : `S/ ${Number(form.precio || 0).toFixed(2)}`}</p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <p className="text-sm font-bold text-slate-700">Contenido · {form.modulos.length} módulos · {totalLecciones} lecciones</p>
                <ul className="mt-2 space-y-2">
                  {form.modulos.map((m, mi) => (
                    <li key={m.id} className="text-sm">
                      <span className="font-semibold text-slate-800">M{mi + 1}. {m.titulo || "Módulo"}</span>
                      <span className="text-slate-400"> · {m.lecciones.length} lecciones</span>
                      {m.lecciones.length > 0 && (
                        <ul className="mt-1 ml-4 space-y-0.5">
                          {m.lecciones.map((l) => (
                            <li key={l.id} className="text-xs text-slate-500">• [{tipoLabel(l.tipo)}] {l.titulo || "Lección"} {l.duracion && `(${l.duracion})`}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
                Estado inicial: <b className="text-slate-800">Borrador</b>. Al enviar a revisión, el equipo de ConstruLink revisará tu curso antes de publicarlo.
              </div>
            </div>
          )}
        </div>

        {/* Navegación */}
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <button onClick={() => (paso === 0 ? navigate("/marketplace/cursos") : setPaso((p) => p - 1))} data-testid="cc-atras-btn" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-100 transition-colors">
            <ChevronLeft size={16} /> {paso === 0 ? "Volver" : "Atrás"}
          </button>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button onClick={() => guardar("borrador")} data-testid="cc-borrador-btn" className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-100 transition-colors">
              <Save size={16} /> Guardar borrador
            </button>
            {paso < PASOS.length - 1 ? (
              <button onClick={siguiente} data-testid="cc-siguiente-btn" className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
                Siguiente <ChevronRight size={16} />
              </button>
            ) : (
              <button onClick={() => guardar("en_revision")} data-testid="cc-enviar-btn" className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
                <Send size={16} /> Enviar a revisión
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
